import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { unstable_batchedUpdates } from "react-dom";

import { serverUrl } from "../../../../config";
import Pagination from "../../../../partials/pagination";
import NoData from "../../../../partials/noData";
import CompanyappLoader from "../../../../partials/companyappLoader";
import useDeckSuspended from "../../../../partials/useDeckSuspended";

import { Container, Row, Col } from "react-bootstrap";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Two full rows of the 8-across grid below.
const PAGE_SIZE = 16;
const ANY = "";
// The cleared value of the one multi-select, kept separate from ANY so a
// `value={NONE}` can't be mistaken for a single-select's empty string. Frozen
// and shared rather than built per render, so the Select's `value` identity is
// stable while nothing is picked.
const NONE = Object.freeze([]);

// The filter bar: one left-aligned row of controls sized by their content
// rather than by Bootstrap's twelfths. Five dropdowns, three number boxes and
// Reset don't divide into 12 columns -- at lg={2} apiece they came to 16 of
// them, which is what pushed Reset onto a second row. Flex instead: the
// dropdowns share whatever is left over, the numbers and Reset take only what
// they need, and the whole thing packs left. `wrap` is the fallback below
// ~1150px, where a single row genuinely doesn't fit.
const FILTER_ROW = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, 1fr)",
    sm: "repeat(4, 1fr)",
    lg: "repeat(8, 1fr)",
  },
  alignItems: "end",
  gap: 1,
};

// Dropdowns grow together, and are deliberately the ones that give up width:
// their labels are short ("Region", "Brand") and their values ellipsis, while
// the number boxes need enough room to spell out "Meeting rooms". 104px is
// where a dropdown label starts to clip.
const SELECT_SLOT = { minWidth: 0 };

// Number boxes never grow: four digits spans the whole catalogue (3,618 guest
// rooms, 5,000 capacity), so extra width would only be white space.
const NUMBER_GROUP = { flex: "0 0 auto" };

// Reset sits at the end of the row, next to the filters it clears.
const RESET_SLOT = { flex: "0 0 auto" };

// The card grid, shared by the results grid and the selection tray so both lay
// out identically. A CSS grid rather than Bootstrap columns because the target
// is 8 across and a 12-column grid has no eighth.
const CARD_GRID = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, 1fr)",
    sm: "repeat(3, 1fr)",
    md: "repeat(6, 1fr)",
    lg: "repeat(8, 1fr)",
  },
  gap: 2,
};
const FILTER_GRID = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, 1fr)",
    sm: "repeat(3, 1fr)",
    md: "repeat(8, 1fr)",
    lg: "repeat(8, 1fr)",
  },
  gap: 2,
};

// The Industries half of the service catalogue: service key -> the label we
// show it under.
//
// Radisson doesn't mark these as industries, so the eight keys are listed here
// and carved out of the service catalogue the filter-options endpoint already
// returns -- no CMS change needed. Its own wording for them is verbose
// ("Association signature events", "Entertainment - Center Stage"), hence our
// own labels.
//
// The keys are upstream's and several have nothing to do with what they tag:
// `train-station` is Automotive, `friends` is Associations, `culture` is
// government travel. Don't "correct" them -- they are what the hotels endpoint
// filters on. Insertion order is the order the dropdown offers them in.
//
// The radisson module mirrors this list (FilterCatalog::INDUSTRIES +
// INDUSTRY_LABELS) for the admin hotel list's own Industry dropdown -- keep
// the two in step if a key or a label changes.
const INDUSTRIES = {
  friends: "Associations",
  "e-sport": "Esports",
  "incentive-travels": "Incentive Travel",
  "train-station": "Automotive",
  "dance-floor-on-request": "Entertainment",
  culture: "Govt Employees",
  "sports-approved": "Sports Approved",
  "m-e-dedicated-staff": "Healthcare",
};

// Matches ../filters.js, so the controls here render identically to the ones
// on "Add slide from library".
const INPUT_FONT_SIZE = 14;
const INPUT_VARIANT = "outlined";

// The number filters are narrow enough that a 14px label would be clipped by
// its own notch, so they label a size down.
const NUMBER_LABEL_FONT_SIZE = 11;

// How many hero documents to pull at once. Each is ~43KB of JSON parsed on the
// main thread purely to read one image URL, so firing a whole page of them at once
// stalls interaction for as long as they take to land. Six keeps the network
// busy while leaving the main thread responsive, and cards fill in as they
// arrive instead of all-or-nothing on the slowest response.
const THUMB_CONCURRENCY = 6;

// "Select All N results" walks the whole set a chunk at a time — the endpoint
// caps `limit` at 1000 — and gives up rather than sitting on a disabled button
// if the CMS never answers.
const SELECT_ALL_CHUNK = 200;
const SELECT_ALL_TIMEOUT = 30000;

// Hides both "Select All N results" buttons — the page one and the cross-page
// one. Hotels are picked a card at a time for now; the walk behind the buttons
// is left in place.
const SHOW_SELECT_ALL = false;

// Reset has to hold its label on a single line at every width rather than
// growing tall and narrow: nowrap plus a content-derived min-width stops the
// label breaking instead of the button widening.
const ACTION_BTN_WRAP = { minWidth: "fit-content" };
const ACTION_BTN = { whiteSpace: "nowrap", px: 1.25, minWidth: 0 };

// The two slides this picker can create. The key travels with each selected
// hotel and editPresentation maps it to the theme layout it creates
// (dynamic-at-a-glance / dynamic-at-a-glance-me), so nothing about the layouts
// is known here. Leisure and M&E are independent: picking both for one hotel
// is two slides, which is the point of having two buttons.
export const HOTEL_SLIDE_LEISURE = "leisure";
export const HOTEL_SLIDE_ME = "me";

const VARIANT_LABEL = {
  [HOTEL_SLIDE_LEISURE]: "Leisure",
  [HOTEL_SLIDE_ME]: "Meetings",
};

const entryKey = (code, variant) => `${code}:${variant}`;

// The two in-card actions. Radisson red comes off the theme's `rhg` palette
// rather than a hex literal here. Sized down hard because eight cards across
// leaves each one narrow enough that a default-size MUI button wraps.
const CARD_ACTION_BTN = {
  flex: "1 1 0",
  minWidth: 0,
  px: 0.5,
  py: 0.25,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.45,
  letterSpacing: 0,
  textTransform: "none",
  whiteSpace: "nowrap",
  borderRadius: "12px",
  boxShadow: "none",
  backgroundColor: "rhg.main",
  color: "rhg.contrastText",
  "&:hover": { backgroundColor: "rhg.dark", boxShadow: "none" },
};

// Each in-card action owns exactly half the row, whether or not it renders
// anything. A tray card shows only the variant it stands for, and without a
// placeholder for the other half that single button would stretch to the full
// width and sit at a different size on every card.
const CARD_ACTION_SLOT = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Stands in for the Meetings button on a hotel that has no M&E offering.
const CARD_ACTION_NA = {
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.45,
  letterSpacing: 0,
  textAlign: "center",
  whiteSpace: "nowrap",
  color: "error.main",
};

const HIDE_NATIVE_SPINNER = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

const CHEVRON_BTN = {
  p: 0,
  height: 10,
  width: 14,
  borderRadius: 0,
  color: "rgba(0, 0, 0, 0.54)",
  "&:hover": { color: "rgba(0, 0, 0, 0.87)", backgroundColor: "transparent" },
};

// The hotels endpoint has no image column — the hero lives in each hotel's
// stored document, which `detailUrl` serves as a static ~43KB file. Insert uses
// /hotel-detail instead, because only that carries the categorised `images` the
// block reads.
const heroFromStoredDoc = (doc) =>
  doc?.hotel?.content?.image?.binaryContent?.url ||
  doc?.content?.image?.binaryContent?.url ||
  "";

// Whether a hotel runs meetings & events at all.
//
// `isMeetingAndEvent` exists only on hotel-detail's roomInfo metadata. The
// hotels list carries meeting_rooms but not this flag, and the stored document
// the hero comes from does not have it either, so it costs a request per card
// — `parts=roomInfo` keeps that to ~18KB rather than the 105KB full document.
// If the list endpoint ever grows the field this whole lookup can go.
//
// It is genuinely independent of the room count: GBLPLFCH, GBBATHEN and
// FRPARYAC all report no meeting rooms with the flag still true, so the count
// alone cannot stand in for it.
//
// Resolves true/false when the answer is known and null when it is not, so a
// failed lookup reads as "unknown" rather than "no".
const fetchMeetingFlag = (code) =>
  fetch(
    `${serverUrl}/api/v2/rhg/hotel-detail?code=${encodeURIComponent(
      code,
    )}&parts=roomInfo&_format=json`,
    { credentials: "include" },
  )
    .then((r) => (r.ok ? r.json() : null))
    .then((doc) =>
      doc
        ? doc?.roomInfo?.collection?.[0]?.metadata?.isMeetingAndEvent === true
        : null,
    )
    .catch(() => null);

// A multi-select's rows carry a tick-box, which supplies the left-hand gap an
// option would otherwise need padding for.
const CHECK_MENU_ITEM = { fontSize: INPUT_FONT_SIZE, pl: 0.5 };
const CHECK_BOX = { p: 0.5, mr: 0.5 };

// "Any" has no tick-box of its own, so in multi-select mode it takes padding
// where the other rows have one: 4px of row padding + a 28px small Checkbox +
// its 4px margin is where their labels start, and without this it hangs left
// of the column it heads.
const CHECK_ANY_ITEM = { fontSize: INPUT_FONT_SIZE, pl: "36px" };
const ANY_ITEM = { fontSize: INPUT_FONT_SIZE };

/**
 * One filter dropdown.
 *
 * Same shape as the Library/Language/Theme controls in ../filters.js -- a
 * primary.light FormControl wrapping an outlined Select -- but laid out as a
 * flex slot rather than a grid column, so the whole bar fits one row (see
 * FILTER_ROW).
 *
 * `multiple` switches it to a tick-box multi-select over an array `value`; the
 * "Any" row then means clear-all rather than a value of its own (see
 * AddHotelSlide's onStarsChange, which is what reads it back out). Everything
 * else -- sizing, label, the ellipsing outlined input -- is shared, so the one
 * multi-select in the bar sits flush with the six single ones.
 *
 * Memoised, and fed a memoised `items` array plus a stable `onChange`, so the
 * parent's frequent re-renders — thumbnails streaming in, selections changing,
 * insert progress ticking — skip all five of these entirely instead of
 * re-rendering five MUI control shells each time.
 */
const FilterSelect = React.memo(function FilterSelect({
  id,
  label,
  value,
  onChange,
  items,
  multiple = false,
}) {
  return (
    <Box className="filter-item" sx={SELECT_SLOT}>
      <FormControl
        className="w-100"
        fullWidth
        size="small"
        sx={{ backgroundColor: "primary.light" }}
      >
        <InputLabel
          id={`${id}-filter-label`}
          sx={{
            fontSize: INPUT_FONT_SIZE,
            color: "rgba(0, 0, 0, 0.87)",
          }}
          size="small"
        >
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-filter-label`}
          id={`${id}-filter`}
          multiple={multiple}
          value={value}
          onChange={onChange}
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          input={
            INPUT_VARIANT === "outlined" ? (
              <OutlinedInput
                owner="admin"
                sub_variant="presentation-filter"
                sx={{
                  fontSize: INPUT_FONT_SIZE,
                  // Narrow slots: a long city name ellipses instead of
                  // stretching the control out of the row.
                  "& .MuiSelect-select": {
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  },
                }}
                label={label}
              />
            ) : (
              <Input
                sx={{
                  fontSize: INPUT_FONT_SIZE,
                  color: "red",
                }}
                label={label}
              />
            )
          }
        >
          {/* In multi-select mode this is the clear-all rather than a value
              of its own -- it comes back through onChange as an empty string
              among the picked ones, which the handler reads as "no filter". */}
          <MenuItem value={ANY} sx={multiple ? CHECK_ANY_ITEM : ANY_ITEM}>
            Any
          </MenuItem>
          {items}
        </Select>
      </FormControl>
    </Box>
  );
});

/**
 * The free-text search box, deliberately a component of its own.
 *
 * It keeps the keystroke-by-keystroke value in its own state and only tells the
 * parent once typing settles. Holding that value on AddHotelSlide meant every
 * character re-rendered the entire modal — five MUI Selects, the button row,
 * the grid and the tray — and MUI recomputes emotion styles on each of those,
 * which is what made typing lag. Memoised so the parent's own re-renders
 * (thumbnails streaming in, selections changing) don't reach back into it.
 */
const HotelSearchField = React.memo(function HotelSearchField({ onSearch }) {
  const [value, setValue] = useState("");

  // Kept in a ref so changing the handler identity can't restart the timer
  // mid-type.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    const t = setTimeout(() => onSearchRef.current(value.trim()), 400);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <FormControl fullWidth size="small">
      <TextField
        id="hotel-title"
        label="Search Hotel Name"
        className="title-textfield"
        variant="standard"
        value={value}
        size="small"
        placeholder="Search hotel name"
        onChange={(event) => setValue(event.target.value)}
        InputProps={{
          style: { fontSize: INPUT_FONT_SIZE },
          endAdornment: (
            <InputAdornment position="end">
              {value.length > 0 ? (
                <IconButton
                  className="title-clear-btn"
                  onClick={() => setValue("")}
                >
                  <ClearIcon className="title-adornment-icon" />
                </IconButton>
              ) : (
                <SearchOutlined className="title-adornment-icon search" />
              )}
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ style: { fontSize: INPUT_FONT_SIZE } }}
      />
    </FormControl>
  );
});

// Each number input is sized to its content -- four digits and a spinner --
// rather than to a dropdown's width.
const NUMBER_FIELD = {
  flex: "0 0 auto",
  width: 100,
  backgroundColor: "primary.light",
  // Trims the default 14px of side padding; the spinner needs the room more
  // than the digits do.
  "& .MuiOutlinedInput-input": { padding: "8.5px 4px 8.5px 8px" },
};

/**
 * One "minimum" number input — guest rooms, meeting rooms, meeting capacity.
 *
 * Renders bare (no column of its own) because all three sit in a single shared
 * slot; see the group in the filter row.
 *
 * Holds its own keystrokes and only reports a settled value, for the same
 * reason HotelSearchField does: a value on AddHotelSlide re-renders the whole
 * modal per character. Reset clears it by remounting it on `resetKey`.
 *
 * What it displays and what it filters on are deliberately separate. The box
 * keeps whatever the browser lets through while you type (a half-typed "3." is
 * not an error), and only the debounced callback is sanitised, so the query
 * carries a positive whole number or nothing at all. A 0 means "no minimum" —
 * as a filter it would only strip hotels with no published figure, which is not
 * what typing 0 into a box labelled Min looks like it should do.
 *
 * `title` carries the full wording (Capacity is the only abbreviation) and,
 * once the facet has loaded, the real ceiling for that field.
 *
 * `facet` is the matching /filter-options entry ({min, max, hotels}), and
 * `ready` says the options have loaded. Unlike Industries and Programs, these
 * three can't be derived front-end — the figures have to be synced into the
 * CMS and filtered there. So when the options come back without a facet (a CMS
 * that predates them) or with no stored figures (one that hasn't synced them
 * yet), the input is disabled and says why, rather than accepting a number and
 * silently filtering nothing.
 */
const NumberFilterField = React.memo(function NumberFilterField({
  id,
  label,
  name,
  facet,
  ready,
  onChange,
}) {
  const [value, setValue] = useState("");

  // Kept in a ref so changing the handler identity can't restart the timer
  // mid-type.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const t = setTimeout(() => {
      const n = parseInt(value, 10);
      onChangeRef.current(Number.isFinite(n) && n > 0 ? String(n) : "");
    }, 400);
    return () => clearTimeout(t);
  }, [value]);

  const step = (delta) => {
    const n = parseInt(value, 10);
    setValue(String(Math.max(0, (Number.isFinite(n) ? n : 0) + delta)));
  };

  const disabled = ready && !facet?.hotels;
  const title = disabled
    ? `The CMS has no ${name.toLowerCase()} figures stored yet`
    : `Minimum ${name.toLowerCase()}${facet?.max ? ` (up to ${facet.max})` : ""}`;

  return (
    <FormControl fullWidth size="small">
      <TextField
        id={id}
        label={label}
        type="number"
        className="title-textfield"
        variant="standard"
        value={value}
        disabled={disabled}
        title={title}
        size="small"
        onChange={(event) => setValue(event.target.value)}
        placeholder="0"
        onWheel={(event) => event.target.blur()}
        sx={HIDE_NATIVE_SPINNER}
        inputProps={{
          min: 0,
          step: 1,
          inputMode: "numeric",
          "aria-label": `Minimum ${name.toLowerCase()}`,
        }}
        InputProps={{
          style: { fontSize: INPUT_FONT_SIZE, paddingRight: 0 },
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{ flexDirection: "column", margin:0, marginTop: "-3px", height: "auto" }}
            >
              <IconButton
                tabIndex={-1}
                aria-label={`Increase minimum ${name.toLowerCase()}`}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => step(1)}
                sx={CHEVRON_BTN}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                tabIndex={-1}
                aria-label={`Decrease minimum ${name.toLowerCase()}`}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => step(-1)}
                sx={CHEVRON_BTN}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        InputLabelProps={{ style: { fontSize: INPUT_FONT_SIZE } }}
        owner="admin"
        sub_variant="presentation-filter"
      />
    </FormControl>
  );
});

/**
 * One of the two slide buttons on a card.
 *
 * Lit when that slide is already in the selection, and clicking it toggles
 * just that slide. `stopPropagation` for the tray, where the card body behind
 * it drops the same slide — without it the click would toggle twice and leave
 * the selection exactly as it was.
 */
const VariantButton = React.memo(function VariantButton({
  hotel,
  variant,
  active,
  onToggleVariant,
}) {
  return (
    <Button
      variant="contained"
      disableElevation
      aria-pressed={active}
      onClick={(event) => {
        event.stopPropagation();
        onToggleVariant(hotel, variant);
      }}
      sx={{
        ...CARD_ACTION_BTN,
        ...(active
          ? {
              backgroundColor: "rhg.dark",
              boxShadow: "inset 0 0 0 2px rgba(255, 255, 255, 0.6)",
            }
          : null),
      }}
    >
      {VARIANT_LABEL[variant]}
    </Button>
  );
});

/**
 * One hotel card.
 *
 * Renders as a bare grid item — the parent lays the cards out with CSS grid, so
 * the card carries no column sizing of its own.
 *
 * The Leisure and Meetings buttons each toggle their own slide, so a hotel can
 * be picked for one, the other or both.
 *
 * `variant` switches the card between its two jobs. Undefined is the results
 * grid: both buttons, each lit by `leisureActive` / `meActive`, with
 * `showMeetings` dropping the second one on a theme that has no M&E layout.
 * The card body itself does nothing there — adding is a choice between two
 * slides, so it has to be made on a button rather than by clicking the card
 * and getting whichever one the card happened to mean. Set to one variant is
 * the selection tray, where a card stands for a single picked slide, and there
 * the whole card is clickable because there is only one thing a click can mean:
 * drop that slide. Passing `onToggleVariant` is what makes either interactive.
 *
 * Memoised deliberately: a page holds 16 of these, and without it every
 * selection click re-rendered all 16 card subtrees. Every prop is a primitive
 * or a stable reference for that reason — the handler is passed the hotel and
 * the variant so the parent can keep one callback rather than minting 32
 * closures per render.
 */
const HotelCard = React.memo(function HotelCard({
  hotel,
  hero,
  variant,
  leisureActive,
  meActive,
  showMeetings = true,
  meetingsUnavailable = false,
  onToggleVariant,
}) {
  // In the tray the card stands for one already-picked slide, so it reads as
  // selected whatever the variant; in the grid either button lighting up is
  // enough to mark the hotel as spoken for.
  const active = variant ? true : Boolean(leisureActive || meActive);
  // Tray cards only. A grid card has two buttons and no default slide, so a
  // click on the card body has nothing unambiguous to do.
  const toggleCard =
    variant && onToggleVariant
      ? () => onToggleVariant(hotel, variant)
      : undefined;

  // The right-hand half. A tray card only ever stands for one slide, so it
  // shows the Meetings button when that is the slide it is and nothing when it
  // isn't — the "not available" note belongs to the results grid, where it
  // explains why an action is missing rather than labelling a finished pick.
  let meetingsSlot = <Box sx={CARD_ACTION_SLOT} />;
  if (showMeetings && (!variant || variant === HOTEL_SLIDE_ME)) {
    meetingsSlot =
      meetingsUnavailable && !variant ? (
        <Box sx={CARD_ACTION_SLOT}>
          <Typography sx={CARD_ACTION_NA}>Meetings n/a</Typography>
        </Box>
      ) : (
        <VariantButton
          hotel={hotel}
          variant={HOTEL_SLIDE_ME}
          active={variant ? true : Boolean(meActive)}
          onToggleVariant={onToggleVariant}
        />
      );
  }

  return (
    <Box
      className="hotel-slide-card"
      onClick={toggleCard}
      role={toggleCard ? "button" : undefined}
      tabIndex={toggleCard ? 0 : undefined}
      aria-pressed={toggleCard ? active : undefined}
      onKeyDown={
        toggleCard
          ? (event) => {
              // The card is a div, so Enter and Space have to be wired by hand
              // to match what a real button would do.
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggleCard();
              }
            }
          : undefined
      }
      sx={{
        border: active ? "3px solid" : "1px solid",
        borderColor: active ? "primary.main" : "#d8d8d8",
        borderRadius: 1,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "border-color 0.15s ease-in-out",
        ...(toggleCard
          ? {
              cursor: "pointer",
              "&:hover": {
                borderColor: active ? "primary.main" : "#9aa5b1",
              },
            }
          : null),
      }}
    >
      {/* Always 16:9, whatever ratio the hero itself is: `cover` scales the
          image up until it fills the frame and the overflow is trimmed evenly
          off both edges, so the visible crop stays centred. */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          backgroundColor: "#eceff1",
          backgroundImage: hero ? `url("${hero}")` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Box
        sx={{
          padding: "8px 10px 12px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {/* Location line parked, not deleted — the two actions below need the
            vertical room and the hotel name already carries the identity.
        <Typography
          variant="caption"
          owner="admin"
          sx={{ display: "block", color: "#5f6b76" }}
        >
          {[hotel.city, hotel.country].filter(Boolean).join(", ")}
        </Typography>
        */}
        <Typography
          variant="body2"
          owner="admin"
          color="adminText.dark"
          // Clamped so a long hotel name can't stretch one card and knock the
          // whole row out of alignment.
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "3.75em",
          }}
        >
          {hotel.name}
        </Typography>

        {/* Two fixed halves: Leisure always on the left, Meetings always on
            the right. A half with nothing to show renders as an empty slot
            rather than collapsing, so the remaining button keeps the same
            width and position it has on every other card. */}
        {onToggleVariant && (
          <Stack direction="row" spacing={0.75} sx={{ mt: "auto", pt: 1 }}>
            {!variant || variant === HOTEL_SLIDE_LEISURE ? (
              <VariantButton
                hotel={hotel}
                variant={HOTEL_SLIDE_LEISURE}
                active={variant ? true : Boolean(leisureActive)}
                onToggleVariant={onToggleVariant}
              />
            ) : (
              <Box sx={CARD_ACTION_SLOT} />
            )}
            {meetingsSlot}
          </Stack>
        )}
      </Box>
    </Box>
  );
});

function AddHotelSlide(props) {
  // This dialog is full-screen, so the editor's Reveal deck is entirely hidden
  // behind it -- but it stays mounted and keeps being re-styled on every resize
  // tick, which is what froze the window for seconds at a time.
  useDeckSuspended(props.open);

  // Whether the deck's theme carries the M&E layout. Sidebar does the lookup,
  // and the default is off: a Meetings button on a theme without that layout
  // would collect picks the editor then can't turn into slides.
  const meEnabled = Boolean(props.meEnabled);

  const [filterOptions, setFilterOptions] = useState(null);
  const [region, setRegion] = useState(ANY);
  const [country, setCountry] = useState(ANY);
  const [city, setCity] = useState(ANY);
  const [brand, setBrand] = useState(ANY);
  // Two halves of the one service catalogue: Industries is the curated
  // who-it's-for list, Programs is everything else. Picking one of each means
  // both must match (the endpoint ANDs the two groups).
  const [industry, setIndustry] = useState(ANY);
  const [program, setProgram] = useState(ANY);
  // Stars is a set of exact ratings, not a minimum: a deck is usually "our
  // 4- and 5-star properties", which a minimum can't express without dragging
  // in the whole scale above or below it. A tick-box dropdown rather than a
  // number box because the scale is fixed at 2–5, so free numeric entry would
  // only invite values that can never match. Held as an array of the rating
  // strings, descending, which is both the order the control lists them in and
  // the order they go onto the query string. Hotels Radisson publishes as
  // "N/A" (~9% of the catalogue) have no rating stored and drop out whenever
  // any rating is ticked — the same way the room-count minimums exclude hotels
  // with no figure.
  const [stars, setStars] = useState(NONE);
  // The three room/meeting filters are minimums — "at least this many" — and
  // hold a settled, already-sanitised string straight from their input (see
  // NumberFilterField), so they go onto the query string as-is.
  const [guestRoomsMin, setGuestRoomsMin] = useState(ANY);
  const [meetingRoomsMin, setMeetingRoomsMin] = useState(ANY);
  const [meetingCapacityMin, setMeetingCapacityMin] = useState(ANY);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Bumped by Reset. The search box and the three number inputs own their
  // in-progress values, so the only way to clear them from out here is to give
  // them a fresh mount.
  const [resetKey, setResetKey] = useState(0);

  const [hotels, setHotels] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  // Hotels matching the current filters, across all pages. The endpoint counts
  // before it slices, so this is the real total rather than what came back.
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInserting, setIsInserting] = useState(false);
  // {done, total} while an insert runs. Each hotel is a /hotel-detail call plus
  // a create plus a patch, all serial, so a large selection takes a while and a
  // bare spinner reads as a hang.
  const [insertProgress, setInsertProgress] = useState({ done: 0, total: 0 });

  // One entry per slide to create: { hotel, variant }. Keyed on the pair, not
  // the hotel, so Leisure and Meetings for the same property are two
  // independent picks and two slides.
  const [selected, setSelected] = useState([]);
  // Selecting the whole result set is a request of its own, and a slow one on a
  // wide filter.
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  // code -> hero image url. Kept across pages and filter changes so paging back
  // and forth doesn't refetch what we already have.
  const [thumbs, setThumbs] = useState({});
  const thumbsRef = useRef({});
  // code -> isMeetingAndEvent (true/false), or null where the lookup failed.
  // Filled by the same pass as the thumbnails, and only when the theme offers
  // the M&E slide at all — with no Meetings button to suppress there is
  // nothing to decide and no reason to pay for the request.
  const [meetingFlags, setMeetingFlags] = useState({});
  const meetingFlagsRef = useRef({});

  // O(1) membership for the card grid, one key per hotel+variant pair.
  // `selected` stays an array because insert order is the order slides get
  // created in.
  const selectedKeys = useMemo(
    () =>
      new Set(
        selected.map((e) => entryKey(e.hotel.tms_hotel_code, e.variant)),
      ),
    [selected],
  );

  // Stable across renders, so HotelCard's memoisation actually holds.
  const toggleVariant = useCallback((hotel, variant) => {
    setSelected((prev) => {
      const match = (e) =>
        e.hotel.tms_hotel_code === hotel.tms_hotel_code && e.variant === variant;
      return prev.some(match)
        ? prev.filter((e) => !match(e))
        : [...prev, { hotel, variant }];
    });
  }, []);

  const isMountedRef = useRef(true);
  useEffect(() => {
    // Set on the way in as well as cleared on the way out: a cleanup that only
    // ever flips it false leaves the ref stuck on a remount, and everything
    // guarded by it — insert progress included — then silently does nothing.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onSearch = useCallback((q) => {
    unstable_batchedUpdates(() => {
      setDebouncedSearch(q);
      setPage(1);
    });
  }, []);

  // ─── Filter options (one call, drives all five dropdowns) ────────────────
  useEffect(() => {
    let cancelled = false;
    fetch(`${serverUrl}/api/v2/rhg/filter-options?_format=json`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setFilterOptions(json);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Hotels ──────────────────────────────────────────────────────────────
  // Shared by the grid and by "select all results", so the second one can ask
  // for exactly the set the first is paging through.
  const buildQuery = useCallback(
    (limit, pageNo) => {
      const params = new URLSearchParams({
        limit: String(limit),
        page: String(pageNo),
        _format: "json",
      });
      if (region) params.append("region", region);
      if (country) params.append("country", country);
      if (city) params.append("city", city);
      if (brand) params.append("brand", brand);
      // Industries and Programs are two halves of one catalogue, so both are
      // service keys as far as the endpoint is concerned. NOTE: it matches a
      // `services` list as ANY-of, so picking one from each dropdown widens
      // the results rather than narrowing them. Narrowing needs the endpoint
      // to take the two as separate groups.
      const serviceKeys = [industry, program].filter(Boolean);
      if (serviceKeys.length) params.append("services", serviceKeys.join(","));
      // A set rather than a minimum: stars_in=5,4 matches exactly 5- and
      // 4-star hotels and nothing between. Hotels with no published rating
      // have NULL in the column and so never match, which is the wanted
      // behaviour — "4-star" cannot include a hotel nobody rated. (The
      // endpoint still takes stars_min; nothing here sends it.)
      if (stars.length) params.append("stars_in", stars.join(","));
      if (guestRoomsMin) params.append("guest_rooms_min", guestRoomsMin);
      if (meetingRoomsMin) params.append("meeting_rooms_min", meetingRoomsMin);
      if (meetingCapacityMin) {
        params.append("meeting_capacity_min", meetingCapacityMin);
      }
      if (debouncedSearch) params.append("q", debouncedSearch);
      return params.toString();
    },
    [
      region,
      country,
      city,
      brand,
      industry,
      program,
      stars,
      guestRoomsMin,
      meetingRoomsMin,
      meetingCapacityMin,
      debouncedSearch,
    ],
  );

  const fetchHotels = useCallback(() => {
    setIsLoading(true);

    fetch(`${serverUrl}/api/v2/rhg/hotels?${buildQuery(PAGE_SIZE, page)}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        // React 17 doesn't batch state set from a promise callback, so without
        // this the three updates below are three separate render passes of the
        // whole grid.
        unstable_batchedUpdates(() => {
          setHotels(json?.collection || []);
          setPages(json?.pages || 1);
          setTotal(json?.total || 0);
          setIsLoading(false);
        });
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setHotels([]);
          setTotal(0);
          setIsLoading(false);
        });
      });
  }, [buildQuery, page]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  // Any filter change puts us back on page 1 — staying on page 9 of a result
  // set that now has two pages shows an empty grid.
  // One stable handler per filter, so FilterSelect's memoisation holds.
  //
  // Region and Country also clear what they narrow: leaving a Country selected
  // from a different Region filtered every hotel out, and left the Select
  // holding a value no longer present in its options.
  const onRegionChange = useCallback((event) => {
    setRegion(event.target.value);
    setCountry(ANY);
    setCity(ANY);
    setPage(1);
  }, []);

  const onCountryChange = useCallback((event) => {
    setCountry(event.target.value);
    setCity(ANY);
    setPage(1);
  }, []);

  const onCityChange = useCallback((event) => {
    setCity(event.target.value);
    setPage(1);
  }, []);

  // The one multi-select, so the one handler that takes an array. "Any" is
  // the clear-all: it arrives as an empty string alongside whatever was
  // already ticked, and means the user asked for no star filter rather than
  // for a rating of "". Sorted descending so the control's summary and the
  // query string both read top-of-scale first, and so a given set of ratings
  // always produces the same URL whatever order they were ticked in.
  const onStarsChange = useCallback((event) => {
    const picked = event.target.value;
    setStars(
      picked.includes(ANY)
        ? NONE
        : [...picked].sort((a, b) => Number(b) - Number(a)),
    );
    setPage(1);
  }, []);

  const onBrandChange = useCallback((event) => {
    setBrand(event.target.value);
    setPage(1);
  }, []);

  const onIndustryChange = useCallback((event) => {
    setIndustry(event.target.value);
    setPage(1);
  }, []);

  const onProgramChange = useCallback((event) => {
    setProgram(event.target.value);
    setPage(1);
  }, []);

  // These take the settled value itself rather than an event — the number
  // inputs debounce internally and hand over a sanitised string.
  const onGuestRoomsChange = useCallback((min) => {
    setGuestRoomsMin(min);
    setPage(1);
  }, []);

  const onMeetingRoomsChange = useCallback((min) => {
    setMeetingRoomsMin(min);
    setPage(1);
  }, []);

  const onMeetingCapacityChange = useCallback((min) => {
    setMeetingCapacityMin(min);
    setPage(1);
  }, []);

  // ─── Card thumbnails ─────────────────────────────────────────────────────
  // Pulled through a small worker pool and flushed on a timer: one render per
  // ~200ms of arrivals rather than one per hotel, which on React 17 (no
  // automatic batching in promises) would be one full grid render per hotel.
  useEffect(() => {
    let cancelled = false;
    // `.current` is only ever mutated, never reassigned, so holding the same
    // object for the life of the effect (cleanup included) is correct.
    const store = thumbsRef.current;
    const meStore = meetingFlagsRef.current;
    const queue = hotels.filter(
      (h) => h.detailUrl && store[h.tms_hotel_code] === undefined,
    );
    if (!queue.length) return undefined;

    // Mark in-flight up front so a re-render mid-fetch doesn't queue the same
    // hotel twice. Anything still unresolved when this effect is torn down gets
    // un-marked below, otherwise changing filters mid-flight would leave those
    // hotels permanently image-less on the way back.
    queue.forEach((h) => {
      store[h.tms_hotel_code] = "";
    });
    const resolved = new Set();

    let flushTimer = null;
    const scheduleFlush = () => {
      if (flushTimer || cancelled) return;
      flushTimer = setTimeout(() => {
        flushTimer = null;
        if (cancelled) return;
        setThumbs({ ...store });
        setMeetingFlags({ ...meStore });
      }, 200);
    };

    let cursor = 0;
    const worker = () => {
      if (cancelled || cursor >= queue.length) return Promise.resolve();
      const h = queue[cursor++];
      // Both lookups for one hotel together, so the pool size still bounds how
      // many hotels are in flight rather than how many requests are.
      return Promise.all([
        fetch(h.detailUrl)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
        meEnabled ? fetchMeetingFlag(h.tms_hotel_code) : Promise.resolve(null),
      ])
        .then(([doc, hasMeetings]) => {
          store[h.tms_hotel_code] = heroFromStoredDoc(doc);
          meStore[h.tms_hotel_code] = hasMeetings;
          resolved.add(h.tms_hotel_code);
          scheduleFlush();
        })
        .catch(() => {})
        .then(worker);
    };

    Promise.all(
      Array.from({ length: Math.min(THUMB_CONCURRENCY, queue.length) }, worker),
    ).then(() => {
      if (cancelled) return;
      setThumbs({ ...store });
      setMeetingFlags({ ...meStore });
    });

    return () => {
      cancelled = true;
      if (flushTimer) clearTimeout(flushTimer);
      queue.forEach((h) => {
        if (!resolved.has(h.tms_hotel_code)) {
          delete store[h.tms_hotel_code];
        }
      });
    };
    // meEnabled is fixed for the life of the dialog (it comes from the deck's
    // theme), so it is not a trigger — listing it would only re-run the pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels]);

  // ─── Cascading options ───────────────────────────────────────────────────
  // Memoised because these lists are large — 477 cities, 102 services, 93
  // countries — and rebuilding ~690 MenuItem elements on every keystroke was
  // the bulk of the typing lag.
  const regionItems = useMemo(
    () =>
      (filterOptions?.regions || []).map((r) => (
        <MenuItem key={r} value={r} sx={{ fontSize: INPUT_FONT_SIZE }}>
          {r}
        </MenuItem>
      )),
    [filterOptions],
  );

  const countryItems = useMemo(
    () =>
      (filterOptions?.countries || [])
        .filter((c) => !region || c.region === region)
        .map((c) => (
          <MenuItem
            key={c.name}
            value={c.name}
            sx={{ fontSize: INPUT_FONT_SIZE }}
          >
            {c.name}
          </MenuItem>
        )),
    [filterOptions, region],
  );

  const cityItems = useMemo(
    () =>
      (filterOptions?.cities || [])
        .filter(
          (c) =>
            (!country || c.country === country) &&
            (!region || c.region === region),
        )
        .map((c) => (
          <MenuItem
            key={`${c.name}-${c.country}`}
            value={c.name}
            sx={{ fontSize: INPUT_FONT_SIZE }}
          >
            {c.name}
          </MenuItem>
        )),
    [filterOptions, country, region],
  );

  // One tick-box per rating the catalogue actually carries, descending —
  // picking hotels for a deck starts at the top of the scale.
  //
  // Preferably from the facet's own `values` (the distinct ratings stored, one
  // entry each), so a rating no hotel holds is never offered a tick-box that
  // could only ever return nothing. A CMS predating that key still sends
  // {min, max, hotels}, which only bounds the scale — fall back to the whole
  // span between them there. Either way an unsynced dataset (hotels: 0)
  // renders no options at all, which is what hides the control.
  const starValues = useMemo(() => {
    const facet = filterOptions?.stars;
    if (!facet?.hotels) return [];
    if (Array.isArray(facet.values) && facet.values.length) {
      return facet.values
        .map((v) => Number(v?.value))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => b - a);
    }
    if (facet.min == null || facet.max == null) return [];
    const span = [];
    for (let n = facet.max; n >= facet.min; n--) span.push(n);
    return span;
  }, [filterOptions]);

  // Unlike the other six, these rows have to re-render when the selection
  // changes — each carries its own tick-box. Cheap: four options against the
  // 477 a city list builds.
  const starsItems = useMemo(
    () =>
      starValues.map((n) => (
        <MenuItem key={n} value={String(n)} sx={CHECK_MENU_ITEM}>
          <Checkbox
            size="small"
            disableRipple
            sx={CHECK_BOX}
            checked={stars.includes(String(n))}
          />
          {n}
        </MenuItem>
      )),
    [starValues, stars],
  );

  const brandItems = useMemo(
    () =>
      (filterOptions?.brands || []).map((b) => (
        <MenuItem
          key={b.code}
          value={b.code}
          sx={{ fontSize: INPUT_FONT_SIZE }}
        >
          {b.name}
        </MenuItem>
      )),
    [filterOptions],
  );

  // Industries: our eight, in our order, minus any key the catalogue doesn't
  // actually carry (so an option can't sit there matching nothing).
  const industryItems = useMemo(() => {
    const catalogue = new Set(
      (filterOptions?.services || []).map((s) => s.key),
    );
    return Object.entries(INDUSTRIES)
      .filter(([key]) => catalogue.has(key))
      .map(([key, name]) => (
        <MenuItem key={key} value={key} sx={{ fontSize: INPUT_FONT_SIZE }}>
          {name}
        </MenuItem>
      ));
  }, [filterOptions]);

  // Programs: the rest of the catalogue, in Radisson's wording and order.
  const programItems = useMemo(
    () =>
      (filterOptions?.services || [])
        .filter((s) => !INDUSTRIES[s.key])
        .map((s) => (
          <MenuItem
            key={s.key}
            value={s.key}
            sx={{ fontSize: INPUT_FONT_SIZE }}
          >
            {s.name}
          </MenuItem>
        )),
    [filterOptions],
  );

  const clearAll = () => setSelected([]);

  const filtersActive = Boolean(
    region ||
    country ||
    city ||
    brand ||
    industry ||
    program ||
    stars.length ||
    guestRoomsMin ||
    meetingRoomsMin ||
    meetingCapacityMin ||
    debouncedSearch,
  );

  const resetFilters = useCallback(() => {
    unstable_batchedUpdates(() => {
      setRegion(ANY);
      setCountry(ANY);
      setCity(ANY);
      setBrand(ANY);
      setIndustry(ANY);
      setProgram(ANY);
      setStars(NONE);
      setGuestRoomsMin(ANY);
      setMeetingRoomsMin(ANY);
      setMeetingCapacityMin(ANY);
      setDebouncedSearch("");
      setResetKey((k) => k + 1);
      setPage(1);
    });
  }, []);

  // Appends rather than replaces, and skips what is already in — selection
  // survives paging, so the same hotel can easily come round twice. Adds the
  // Leisure slide: a bulk pick has no way to say which variant it means, and
  // the standard one is what "select all these hotels" has always produced.
  const addToSelection = useCallback((rows) => {
    setSelected((prev) => {
      const next = [...prev];
      const have = new Set(
        prev.map((e) => entryKey(e.hotel.tms_hotel_code, e.variant)),
      );
      rows.forEach((h) => {
        if (!have.has(entryKey(h.tms_hotel_code, HOTEL_SLIDE_LEISURE))) {
          next.push({ hotel: h, variant: HOTEL_SLIDE_LEISURE });
        }
      });
      return next;
    });
  }, []);

  const selectAllOnPage = () => addToSelection(hotels);

  // Everything the current filters match, not just the page on screen.
  //
  // Walked in chunks rather than asked for in one go: the endpoint caps `limit`
  // at 1000, so a single limit=total request would silently hand back a short
  // list on any set bigger than that and the button would under-deliver on the
  // count in its own label.
  //
  // The button is disabled for the duration, so the one thing this must never
  // do is leave without clearing the flag — hence the abort timer and the
  // unconditional reset in the tail.
  const selectAllResults = useCallback(() => {
    if (!total || isSelectingAll) return;
    setIsSelectingAll(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SELECT_ALL_TIMEOUT);
    const rows = [];

    const fetchPage = (pageNo) =>
      fetch(
        `${serverUrl}/api/v2/rhg/hotels?${buildQuery(SELECT_ALL_CHUNK, pageNo)}`,
        { credentials: "include", signal: controller.signal },
      )
        .then((r) => {
          if (!r.ok) throw new Error(`hotels ${r.status}`);
          return r.json();
        })
        .then((json) => {
          const batch = json?.collection || [];
          rows.push(...batch);
          // Trust the response's own total over the one in state, which was
          // read on a different request.
          const expected = json?.total || 0;
          return batch.length === SELECT_ALL_CHUNK && rows.length < expected
            ? fetchPage(pageNo + 1)
            : rows;
        });

    fetchPage(1)
      .then((all) => {
        if (isMountedRef.current) addToSelection(all);
      })
      .catch((error) => {
        console.error("Select all hotels failed", error);
      })
      .then(() => {
        clearTimeout(timeout);
        if (isMountedRef.current) setIsSelectingAll(false);
      });
  }, [addToSelection, buildQuery, isSelectingAll, total]);

  // What the loader and the Insert button both say while a job runs. `done`
  // counts hotels finished, so the one being worked on is done + 1; once it
  // reaches the total the slides exist and what's left is the deck patch and
  // reload, which is its own few seconds.
  const insertLabel = useMemo(() => {
    const { done, total: count } = insertProgress;
    if (!count) return "Inserting...";
    if (done >= count) return "Finishing up...";
    if (count === 1) return "Inserting slide";
    return `Inserting ${done + 1} of ${count} slides`;
  }, [insertProgress]);

  const onInsert = () => {
    if (!selected.length || isInserting) return;

    const done = () => {
      // A successful insert reloads the deck, which unmounts this dialog before
      // the promise settles — so only touch state if we're still here.
      if (!isMountedRef.current) return;
      unstable_batchedUpdates(() => {
        setIsInserting(false);
        setInsertProgress({ done: 0, total: 0 });
      });
    };

    unstable_batchedUpdates(() => {
      setIsInserting(true);
      setInsertProgress({ done: 0, total: selected.length });
    });

    Promise.resolve(
      props.onAddHotelSlides(selected, (doneCount, total) => {
        if (isMountedRef.current) {
          setInsertProgress({ done: doneCount, total });
        }
      }),
    )
      .then(done)
      .catch(done);
  };

  return (
    <Dialog
      fullScreen={true}
      open={props.open}
      onClose={() => {
        if (!isInserting) props.onClose();
      }}
      className="presentation-library-modal hotel-slide-library-modal"
    >
      {/* The same loader the editor puts up while a library slide insert runs
          (editPresentation's `isLoading` overlay). That one is already running
          underneath — addHotelSlides sets loading on entry — but this dialog is
          above it, so it repeats it here. Fixed rather than absolute: it has to
          clear the AppBar's own stacking context. */}
      {isInserting && (
        <Box
          className="circular-container fs"
          sx={{
            position: "fixed",
            zIndex: (theme) => theme.zIndex.modal + 1,
            // Denser than the 75% white the class carries — over a
            // full-screen white dialog that veil barely registers as "busy,
            // nothing here is clickable" — while still showing what is behind
            // it. !important because that class wins on specificity.
            backgroundColor: "rgba(255, 255, 255, 0.9) !important",
            color: "#000",
            flexDirection: "column",
            gap: 2,
            textAlign: "center",
            px: 3,
          }}
        >
          <CircularProgress size={28} color="primary" />
          <Typography variant="h6" sx={{ color: "inherit" }}>
            {insertLabel}
          </Typography>
        </Box>
      )}

      <AppBar
        sx={{
          position: "relative",
          backgroundColor: "primary.light",
          color: "solid.dark",
        }}
      >
        <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Row className="w-100 d-flex align-items-center">
              <Col>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Dynamic Hotel Slide Library
                </Typography>
              </Col>
              <Col className="d-flex justify-content-end align-items-center">
                <Row>
                  <Col className="d-flex justify-content-end">
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="cancel"
                        disabled={isInserting}
                        onClick={() => props.onClose()}
                        sx={{ marginRight: "10px !important" }}
                      >
                        <Typography
                          variant="button"
                          owner="admin"
                          className="lh"
                        >
                          Cancel
                        </Typography>
                      </Button>
                      <Button
                        variant="contained"
                        owner="admin"
                        dialogbtn="true"
                        dialogvariant="save"
                        className="lh"
                        disabled={!selected.length || isInserting}
                        startIcon={
                          isInserting ? (
                            <CircularProgress
                              size={16}
                              color="inherit"
                              // Indeterminate until the first hotel is
                              // reported, so the ring doesn't sit at 0%.
                              variant={
                                insertProgress.done
                                  ? "determinate"
                                  : "indeterminate"
                              }
                              value={
                                insertProgress.total
                                  ? (insertProgress.done /
                                      insertProgress.total) *
                                    100
                                  : 0
                              }
                            />
                          ) : null
                        }
                        onClick={onInsert}
                      >
                        <Typography
                          variant="button"
                          owner="admin"
                          className="lh"
                        >
                          {isInserting
                            ? insertLabel
                            : `Insert${
                                selected.length ? ` (${selected.length})` : ""
                              }`}
                        </Typography>
                      </Button>
                    </Stack>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Toolbar>
        </DialogContent>
      </AppBar>

      <DialogContent
        sx={{ textAlign: isLoading && !hotels.length ? "center" : "" }}
      >
        <Container fluid className="add-library-container">
          <div className="ps-filters">
            <Box
              className="page-header mb-1 mt-1"
              sx={{ ...CARD_GRID, alignItems: "end" }}
            >
              <Box
                className="title-container"
                sx={{
                  gridColumn: {
                    xs: "span 2",
                    sm: "span 3",
                    md: "span 6",
                    lg: "span 3",
                  },
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  columnGap: 2,
                }}
              >
                <Typography
                  variant="h1"
                  owner="admin"
                  color="clientText.dark"
                  className="mb-0"
                >
                  Choose hotel slide
                </Typography>
                {total > 0 && (
                  <Typography
                    variant="body1"
                    owner="admin"
                    sx={{
                      color: "#5f6b76",
                      fontSize: 16,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`Showing ${hotels.length} of ${total}`}
                  </Typography>
                )}
              </Box>

              <NumberFilterField
                key={`guest-rooms-${resetKey}`}
                id="guest-rooms"
                label="Min. Guest rooms"
                name="guest rooms"
                facet={filterOptions?.guest_rooms}
                ready={Boolean(filterOptions)}
                onChange={onGuestRoomsChange}
              />
              <NumberFilterField
                key={`meeting-rooms-${resetKey}`}
                id="meeting-rooms"
                label="Min. Meeting rooms"
                name="meeting rooms"
                facet={filterOptions?.meeting_rooms}
                ready={Boolean(filterOptions)}
                onChange={onMeetingRoomsChange}
              />
              <NumberFilterField
                key={`meeting-capacity-${resetKey}`}
                id="meeting-capacity"
                label="Min. Capacity"
                name="meeting capacity"
                facet={filterOptions?.meeting_capacity}
                ready={Boolean(filterOptions)}
                onChange={onMeetingCapacityChange}
              />

              <Box
                className="general-filters"
                sx={{
                  gridColumn: {
                    xs: "span 1",
                    sm: "span 3",
                    md: "span 3",
                    lg: "span 2",
                  },
                  minWidth: 0,
                }}
              >
                <HotelSearchField key={resetKey} onSearch={onSearch} />
              </Box>
            </Box>

            <Box className="filter-container" sx={{ ...FILTER_GRID }}>
              <FilterSelect
                id="region"
                label="Region"
                value={region}
                onChange={onRegionChange}
                items={regionItems}
              />
              <FilterSelect
                id="country"
                label="Country"
                value={country}
                onChange={onCountryChange}
                items={countryItems}
              />
              <FilterSelect
                id="city"
                label="City"
                value={city}
                onChange={onCityChange}
                items={cityItems}
              />
              <FilterSelect
                id="brand"
                label="Brand"
                value={brand}
                onChange={onBrandChange}
                items={brandItems}
              />
              <FilterSelect
                id="industry"
                label="Industries"
                value={industry}
                onChange={onIndustryChange}
                items={industryItems}
              />
              <FilterSelect
                id="program"
                label="Features"
                value={program}
                onChange={onProgramChange}
                items={programItems}
              />
              {/* Hidden entirely when the catalogue carries no ratings — an
                  empty dropdown is worse than no dropdown. */}
              {starsItems.length > 0 && (
                <FilterSelect
                  id="stars"
                  label="Stars"
                  multiple
                  value={stars}
                  onChange={onStarsChange}
                  items={starsItems}
                />
              )}
              {/* The three minimums, packed together — a four-digit box needs
                  a fraction of a dropdown's width. Keyed on resetKey so Reset
                  clears what each input is holding internally. */}

              {/* Last in the row, next to the filters it clears. The
                  selection buttons live down by the pagination, next to the
                  results they act on. */}
              <Box className="filter-item" sx={SELECT_SLOT}>
                <FormControl className="btn w-100" fullWidth>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={ACTION_BTN}
                    disabled={!filtersActive}
                    onClick={resetFilters}
                  >
                    <Typography owner="admin" variant="button" className="lh">
                      Reset
                    </Typography>
                  </Button>
                </FormControl>
              </Box>
            </Box>

            {/* Selecting the page sits with the filters because that is what
                changes what "the page" is. The count it refers to moved to the
                foot of the grid. Both buttons are off for now — see
                SHOW_SELECT_ALL. */}
            {SHOW_SELECT_ALL && total > 0 && (
              <Row className="mt-1">
                <Col>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Button
                      variant="outlined"
                      sx={{ ...ACTION_BTN, fontSize: INPUT_FONT_SIZE }}
                      disabled={!hotels.length}
                      onClick={selectAllOnPage}
                    >
                      <Typography owner="admin" variant="button" className="lh">
                        {`Select All ${hotels.length} results`}
                      </Typography>
                    </Button>

                    {/* Hidden for now. Takes every page behind the current
                        one, and was only offered once a selection was under
                        way and the results were cut down to a country. */}
                    {selected.length > 0 && country !== ANY && (
                      <Button
                        variant="outlined"
                        sx={{ ...ACTION_BTN, fontSize: INPUT_FONT_SIZE }}
                        disabled={isSelectingAll}
                        startIcon={
                          isSelectingAll ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : null
                        }
                        onClick={selectAllResults}
                      >
                        <Typography
                          owner="admin"
                          variant="button"
                          className="lh"
                        >
                          {`Select All ${total} results`}
                        </Typography>
                      </Button>
                    )}
                  </Stack>
                </Col>
              </Row>
            )}
          </div>

          {isLoading && !hotels.length ? (
            <div className="circular-container fs t">
              <CompanyappLoader />
            </div>
          ) : (
            <>
              <Box className="mt-1" sx={{ position: "relative" }}>
                {hotels.length > 0 ? (
                  <>
                    {/* Paging/refiltering veils the current grid rather than
                        collapsing it to a spinner, so the scroll position
                        doesn't jump. Same as the library modal. */}
                    {isLoading && (
                      <Box
                        className="w-100 h-100"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                          zIndex: 5,
                          background: "rgba(255, 255, 255, 1)",
                        }}
                      >
                        <CompanyappLoader />
                      </Box>
                    )}
                    <Box sx={CARD_GRID}>
                      {hotels.map((h) => (
                        <HotelCard
                          key={`grid-${h.tms_hotel_code}`}
                          hotel={h}
                          hero={thumbs[h.tms_hotel_code]}
                          leisureActive={selectedKeys.has(
                            entryKey(h.tms_hotel_code, HOTEL_SLIDE_LEISURE),
                          )}
                          meActive={
                            meEnabled &&
                            selectedKeys.has(
                              entryKey(h.tms_hotel_code, HOTEL_SLIDE_ME),
                            )
                          }
                          showMeetings={meEnabled}
                          // Suppressed only on a positive "no": the flag has
                          // landed and says no M&E, and the hotel lists no
                          // meeting rooms. Still loading or a failed lookup
                          // leaves the button, so a slow request never takes
                          // an action away.
                          meetingsUnavailable={
                            meetingFlags[h.tms_hotel_code] === false &&
                            !h.meeting_rooms
                          }
                          onToggleVariant={toggleVariant}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <NoData
                    activeIndex={0}
                    message1="There are no hotels matching those filters."
                  />
                )}
              </Box>

              <Row className="mt-1 mb-1">
                <Col
                  className="d-flex justify-content-center flex-wrap"
                  style={{ position: "relative" }}
                >
                  <Pagination
                    owner="admin"
                    containerClasses="creator-pagination d-flex justify-content-center align-items-center"
                    previousDisabled={page <= 1}
                    nextDisabled={page >= pages}
                    currentPage={page}
                    onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(pages, p + 1))}
                  />
                </Col>
              </Row>

              {selected.length > 0 && (
                <Box
                  sx={{
                    width: 1,
                    backgroundColor: "primary.light",
                    padding: "0 0.75rem",
                  }}
                >
                  <Row className="mt-1 pt-3 align-items-center">
                    <Col>
                      <h5 className="mb-0">
                        Selected hotel slides to insert:{" "}
                        <Typography
                          variant="span"
                          color="primary"
                          className="presentation-info"
                        >
                          {`${selected.length} selected`}
                        </Typography>
                      </h5>
                    </Col>
                    {/* Emptying the tray belongs with the tray, not with the
                        filter bar - up there it read as "clear the filters". */}
                    <Col xs="auto" className="d-flex justify-content-end">
                      <Button
                        variant="outlined"
                        owner="admin"
                        sx={ACTION_BTN}
                        disabled={isInserting}
                        onClick={clearAll}
                      >
                        <Typography
                          owner="admin"
                          variant="button"
                          className="lh"
                        >
                          Clear All
                        </Typography>
                      </Button>
                    </Col>
                  </Row>
                  <Box sx={{ ...CARD_GRID, pt: 3, pb: 3 }}>
                    {selected.map((e) => (
                      /* One card per slide, labelled with the variant it will
                         create, and clicking it drops that slide. Removing one
                         at a time used to mean finding it again in the grid,
                         or Clear All and starting over. */
                      <HotelCard
                        key={`selected-${entryKey(
                          e.hotel.tms_hotel_code,
                          e.variant,
                        )}`}
                        hotel={e.hotel}
                        hero={thumbs[e.hotel.tms_hotel_code]}
                        variant={e.variant}
                        onToggleVariant={toggleVariant}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Container>
      </DialogContent>
    </Dialog>
  );
}

// Memoised because of where this modal lives: <Sidebar>, inside
// <EditPresentation>. Neither of those is memoised, so without this every
// re-render up there -- a slide edit, an autosave, a reveal event -- re-rendered
// the whole library: the filter bar's ~690 <MenuItem>s, both card grids and the
// selection tray. The individual pieces are memoised already, but reconciling
// that tree at all is the cost, and it is pure waste while the modal is just
// sitting open.
//
// This only holds as long as Sidebar hands it stable props, which is why its
// `onClose` and `onAddHotelSlides` are ref-backed useCallbacks there rather
// than the inline arrows every other dialog in that file gets.
export default React.memo(AddHotelSlide);
